import { Info, TrendingDown, TrendingUp } from "lucide-react";
import { Text, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@musa.tech/design-system";
import { useCountUp } from "@/hooks/useCountUp";
import { fmtCompactNumber } from "@/utils/formatCompactNumber";
import { CompactValue } from "./CompactValue";
import { KpiCardShell } from "./KpiCardShell";
import { formatVariationPercent, getVariationTrendDirection } from "./formatVariation";

interface CollectsPerformedCardProps {
  totalCollects: number;
  variationPercent: number | null;
  successRatePercent: number;
  mtrRatePercent: number;
  /** MTR só é exibido para perfis com permission_role (regra pré-existente do KPI antigo). */
  showMtrRate: boolean;
}

/**
 * Card 2 "Coletas realizadas" — variação sempre em neutral-900, nunca
 * verde/vermelho (decisão: seguir o Figma à risca, só o ícone de tendência
 * muda de direção). Layout e cores conferidos no Figma (node 124:13010):
 * chip "valor + ícone" numa única linha ao lado do valor principal, sem
 * legenda "vs período anterior" (não existe no design); linhas de
 * submétrica com ícone de tendência à esquerda e Info à direita.
 */
export const CollectsPerformedCard = ({
  totalCollects,
  variationPercent,
  successRatePercent,
  mtrRatePercent,
  showMtrRate,
}: CollectsPerformedCardProps) => {
  // As submétricas contam junto com o total: se só o número de cima animasse,
  // o card mostraria "0" com "78% de coletas sem falhas" abaixo durante a
  // contagem — leitura contraditória por quase um segundo.
  const animatedCollects = useCountUp(totalCollects);
  const animatedSuccessRate = useCountUp(successRatePercent);
  const animatedMtrRate = useCountUp(mtrRatePercent);

  return (
  <KpiCardShell
    label="Coletas realizadas"
    header={
      <div className="flex items-center justify-between gap-2">
        <Text className="min-w-0 text-2xl font-semibold leading-9 tracking-normal text-neutral-900">
          <CompactValue exact={totalCollects} unit="coletas">
            {fmtCompactNumber(animatedCollects)}
          </CompactValue>
        </Text>
        {variationPercent !== null && (
          <div className="flex min-w-0 items-center gap-2">
            <Text className="min-w-0 text-xs leading-4 text-neutral-900">
              {formatVariationPercent(variationPercent)}
            </Text>
            {getVariationTrendDirection(variationPercent) === "up" ? (
              <TrendingUp className="h-4 w-4 shrink-0 text-neutral-900" />
            ) : (
              <TrendingDown className="h-4 w-4 shrink-0 text-neutral-900" />
            )}
          </div>
        )}
      </div>
    }
    rows={
      <>
        <SubmetricRow
          text={`${Math.round(animatedSuccessRate).toLocaleString("pt-BR")}% de coletas sem falhas`}
          tooltip="Percentual de coletas concluídas com sucesso sobre o total de coletas no período. Reflete a eficiência operacional."
        />
        {showMtrRate && (
          <SubmetricRow
            text={`${Math.round(animatedMtrRate).toLocaleString("pt-BR")}% coletas com MTRs disponíveis`}
            tooltip="Percentual de coletas concluídas com MTR disponível para download na aba Histórico de Coletas, na tela de Coletas."
          />
        )}
      </>
    }
  />
  );
};

const SubmetricRow = ({ text, tooltip }: { text: string; tooltip: string }) => (
  <div className="flex items-center justify-between gap-2">
    <div className="flex min-w-0 items-center gap-2">
      <TrendingUp className="h-4 w-4 shrink-0 text-neutral-700" />
      <Text variant="label" size="small" className="min-w-0 text-neutral-700">
        {text}
      </Text>
    </div>
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-5 w-5 shrink-0 cursor-help text-neutral-700" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
);
